// Plain state container. One object, synchronous subscribers, no
// framework. The renderer diffs `prev` against `next` to decide what to
// touch in the DOM.

import type { Conversation, ConversationDetail, Message } from './types'

export type Phase = 'welcome' | 'loading' | 'chat' | 'history-list' | 'history-detail'

export interface Pending {
  localId: string
  content: string
  /** Local preview of the image being sent along. */
  previewUrl?: string
  /** Id the server gave this message; set once the POST answered. */
  serverId?: string
}

export interface State {
  phase: Phase
  conversation: Conversation | null
  messages: Message[]
  pending: Pending | null
  awaitingReply: boolean
  csatSubmitted: boolean
  verified: boolean
  /** Small line above the transcript, e.g. "Continuing a fresh conversation." */
  notice: string | null
  error: string | null
  history: Conversation[] | null
  historyDetail: ConversationDetail | null
}

export type Patch = Partial<State> | ((s: State) => Partial<State>)

export interface Store {
  get(): State
  set(patch: Patch): void
  subscribe(fn: (next: State, prev: State) => void): () => void
}

export function initialState(verified = false): State {
  return {
    phase: 'welcome',
    conversation: null,
    messages: [],
    pending: null,
    awaitingReply: false,
    csatSubmitted: false,
    verified,
    notice: null,
    error: null,
    history: null,
    historyDetail: null,
  }
}

export function createStore(initial: State): Store {
  let state = initial
  const listeners = new Set<(next: State, prev: State) => void>()
  return {
    get: () => state,
    set(patch) {
      const prev = state
      const delta = typeof patch === 'function' ? patch(prev) : patch
      state = { ...prev, ...delta }
      for (const fn of Array.from(listeners)) {
        try {
          fn(state, prev)
        } catch (err) {
          console.error('[airys widget] subscriber threw:', err)
        }
      }
    },
    subscribe(fn) {
      listeners.add(fn)
      return () => listeners.delete(fn)
    },
  }
}
